import React, { FC, useEffect } from 'react';
import { Button, Form, InputNumber, Modal, Switch } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { IFormValue } from 'common/models';
import { showSuccessMessage } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IContractGoodsSettingsItem } from 'entities/Contract/Contract.models';
import {
  unsetContractGoodSettings,
  updateGoodsAvailability,
  patchGoodSettingsItem,
  selectIsGoodLoading,
} from 'entities/Goods/Good.slice';

interface IComponentProps {
  open: boolean;
  settings: IContractGoodsSettingsItem | null;
  onClose: () => void;
}

export const GoodsAvailabilitySettingsModal: FC<IComponentProps> = ({ open, settings, onClose }) => {
  const dispatch = useAppDispatch();
  // good
  const isGoodLoading = useAppSelector(selectIsGoodLoading);

  const [form] = useForm();

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue({
        price: settings.price,
        isAvailableForCustomer: settings.isAvailableForCustomer,
      });
    }
  }, [open, settings]);

  const handleClose = (e: any) => {
    e.stopPropagation();
    if (isGoodLoading) {
      return;
    }

    onClose();
  };

  const handleUnsetContractGoodsSettings = () => {
    if (!settings) {
      return;
    }

    dispatch(
      unsetContractGoodSettings({
        contractId: settings.contractId,
        goodId: settings.goodId,
        onSuccess: onClose,
      }),
    );
  };

  const onSubmit = ({ price, isAvailableForCustomer }: IFormValue) => {
    if (!settings) {
      return;
    }

    dispatch(
      updateGoodsAvailability({
        contractId: settings.contractId,
        isAvailableForCustomer: isAvailableForCustomer ? true : false,
        goods: [{ goodId: settings.goodId, price }],
        onSuccess: () => {
          dispatch(
            patchGoodSettingsItem({
              ...settings,
              isAvailableForCustomer: isAvailableForCustomer ? true : false,
              price,
              inProgress: true,
              isManuallySet: true,
            }),
          );
          showSuccessMessage('Цена позиции в рамках контракта успешно настроена.');
          onClose();
        },
      }),
    );
  };

  return (
    <Modal
      rootClassName="redesign modal"
      width={480}
      centered
      open={open}
      onCancel={handleClose}
      destroyOnClose
      // @ts-ignore
      styles={{ mask: { zIndex: 1051, background: 'transparent' }, wrapper: { zIndex: 1052 } }}
      classNames={{ footer: 'direction-row-revers justify-between' }}
      footer={[
        <div key="actions" className="flex align-center gap-3">
          <Button className="button-sm secondary" onClick={handleClose} loading={isGoodLoading}>
            Отмена
          </Button>
          <Button className="button-sm primary" loading={isGoodLoading} onClick={form.submit}>
            Сохранить
          </Button>
        </div>,
        settings?.isManuallySet ? (
          <Button
            key="unset"
            className="button-text dark-green"
            loading={isGoodLoading}
            onClick={handleUnsetContractGoodsSettings}
          >
            Сбросить изменения
          </Button>
        ) : null,
      ]}
    >
      <div className="ff-medium fs-m mt-6 mb-3">Цена и доступность</div>

      <div className="ff-regular fs-xs color-dark-grey mb-6">
        {`Настройте цену и доступность позиции в рамках контракта с ${settings?.buyerName}.`}
      </div>

      <Form layout="vertical" requiredMark={false} form={form} onFinish={onSubmit}>
        <Form.Item label="Цена для контракта" name="price">
          <InputNumber placeholder="Введите цену" suffix="₽" controls={false} precision={2} />
        </Form.Item>

        <div className="mb-6 flex align-center justify-between">
          <span className="ff-regular fs-s">Доступно для заказа</span>

          <Form.Item className="mb-0" name="isAvailableForCustomer" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
